import { type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { ActionIconSlot } from '@renderer/components/action-icon-slot'
import { getActionIcon } from '@renderer/overlay/Sidepanel/utils/activity-helpers'
import { askUserCardVariant, isAnswerableStagedAction } from '@renderer/chat/integrations/answerable'
import { AskUserBadge } from '@renderer/chat/integrations/ask-user-card'
import { getReportOriginSkill, OriginSkillIcon } from '@renderer/chat/integrations/report-to-user'
import { SidepanelCellRow } from './cells/row'
import { ActionErrorBadge } from './action-status-badge'
import type { StagedAction } from '../hooks/use-staged-actions'

interface CollapsedActionRowProps {
  action: StagedAction
  title: string
  unread: boolean
  focused: boolean
  /** Swaps the corner badge for a failure marker; the message lives in the hover card. */
  hasError?: boolean
  /**
   * Replaces the corner badge outright. For states the two booleans above don't
   * cover — an executed row's success check.
   */
  subIcon?: ReactNode
  /** Decoration centred on the integration tile — the executed row's expiry countdown. */
  iconOverlay?: ReactNode
  onClick: (event: MouseEvent<HTMLDivElement>) => void
  onKeyDown: (e: KeyboardEvent) => void
  onFocus: () => void
  onBlur: () => void
}

/**
 * An Outbox row reduced to its title, for rows whose detail lives in a hover
 * card. Its own component rather than negations inside the full cell because
 * the collapsed row isn't the full one minus a few blocks — it drops the body,
 * tags, source attribution and CTAs, and swaps the chrome (corner badge, status
 * ring) — so what the card is now solely responsible for stays visible in one
 * place. The Reminder row collapses by hiding three optional blocks in place
 * (`collapsed` in `reminders-cell`), which is well under that bar.
 */
export function CollapsedActionRow({
  action,
  title,
  unread,
  focused,
  hasError,
  subIcon,
  iconOverlay,
  onClick,
  onKeyDown,
  onFocus,
  onBlur,
}: CollapsedActionRowProps) {
  // Neither a question nor a feedback card is a draft against an integration:
  // there's no logo to show, so the row wears the same badge the card it opens
  // does instead of an integration tile. Same treatment the expanded cell gives it.
  const isAnswerable = isAnswerableStagedAction(action)

  // A report attributed to a skill is badged with that skill's icon rather than
  // the integration glyph — the skill is what produced the message.
  const reportSkill = getReportOriginSkill(action)

  const iconSlotProps = {
    overlay: iconOverlay,
    unread: unread && !hasError && !subIcon,
    subIcon: subIcon ?? (hasError ? <ActionErrorBadge /> : <></>),
  }

  return (
    <SidepanelCellRow
      icon={
        isAnswerable ? (
          <AskUserBadge variant={askUserCardVariant(action)} {...iconSlotProps} />
        ) : (
          <ActionIconSlot view={action.view} {...iconSlotProps}>
            {reportSkill ? <OriginSkillIcon skillName={reportSkill} /> : getActionIcon(action)}
          </ActionIconSlot>
        )
      }
      title={title}
      focused={focused}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  )
}
